import { invokeEdgeFunction } from '../integrations/supabase/functions';
import type { AppStoreTransaction } from '../plugins/appStorePurchases';

type SyncAppleTransactionsResult = {
  synced: boolean;
  billingTransitionWarning: string | null;
  latestTransactionId: string | null;
  accountMismatch: boolean;
};

const normalizeAccountToken = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length ? normalized : null;
};

const normalizeAppleEnvironment = (
  environment: string | null | undefined
): 'Sandbox' | 'Production' | null => {
  if (!environment) return null;
  const normalized = environment.toLowerCase();
  if (normalized.includes('sandbox')) return 'Sandbox';
  if (normalized.includes('production')) return 'Production';
  return null;
};

export const syncAppStoreTransactions = async (
  transactions: AppStoreTransaction[],
  ownerUserId?: string
): Promise<SyncAppleTransactionsResult> => {
  if (!transactions.length) {
    return {
      synced: false,
      billingTransitionWarning: null,
      latestTransactionId: null,
      accountMismatch: false,
    };
  }

  const normalizedOwnerUserId = normalizeAccountToken(ownerUserId);
  const uniqueTransactions = Array.from(
    new Map(transactions.map((transaction) => [transaction.transactionId, transaction])).values()
  );
  const accountMatchedTransactions = normalizedOwnerUserId
    ? uniqueTransactions.filter((transaction) => {
        const token = normalizeAccountToken(transaction.appAccountToken);
        return !token || token === normalizedOwnerUserId;
      })
    : uniqueTransactions;

  if (!accountMatchedTransactions.length) {
    return {
      synced: false,
      billingTransitionWarning: null,
      latestTransactionId: null,
      accountMismatch: true,
    };
  }

  const sortedTransactions = [...accountMatchedTransactions].sort((left, right) => {
    const leftExpiry = left.expirationDate ? new Date(left.expirationDate).getTime() : 0;
    const rightExpiry = right.expirationDate ? new Date(right.expirationDate).getTime() : 0;
    return rightExpiry - leftExpiry;
  });

  const latest = sortedTransactions[0];
  if (!latest) {
    return {
      synced: false,
      billingTransitionWarning: null,
      latestTransactionId: null,
      accountMismatch: false,
    };
  }

  let billingTransitionWarning: string | null = null;

  for (const transaction of sortedTransactions) {
    const { data, error } = await invokeEdgeFunction('sync-apple-subscription', {
      body: {
        signedTransactionInfo: transaction.signedTransactionInfo,
        transactionId: transaction.transactionId,
        environment: normalizeAppleEnvironment(transaction.environment),
      },
    });

    if (error) {
      throw error;
    }

    if (!billingTransitionWarning && typeof data?.billingTransitionWarning === 'string') {
      billingTransitionWarning = data.billingTransitionWarning;
    }
  }

  return {
    synced: true,
    billingTransitionWarning,
    latestTransactionId: latest.transactionId,
    accountMismatch: false,
  };
};
